/**
 * Компонент для отображения QR-кода
 */
import { useEffect, useState, useRef } from 'react'
import QRCode from 'qrcode'
import { QRGenerator } from '../services/qrGenerator'
import type { QRGenerationResult } from '../services/qrGenerator'
import './QRDisplay.css'

interface QRDisplayProps {
  className?: string
}

export function QRDisplay({ className }: QRDisplayProps) {
  const [qrData, setQrData] = useState<QRGenerationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null)
  const qrDataRef = useRef<QRGenerationResult | null>(null)
  const retryCountRef = useRef(0) // Вынесено на уровень компонента
  
  // Watermark state for screenshot protection
  const [watermarkId, setWatermarkId] = useState<string>('')
  const [traceElements, setTraceElements] = useState<string[]>([])
  
  // Get employee ID from localStorage
  const employeeId = localStorage.getItem('employee_id') || 'unknown'
  
  // Update watermark every second for screenshot tracing
  useEffect(() => {
    const watermarkInterval = setInterval(() => {
      setWatermarkId(`${employeeId}-${Date.now()}`)
    }, 1000)
    
    return () => clearInterval(watermarkInterval)
  }, [employeeId])
  
  // Update trace elements every 500ms for screenshot detection
  useEffect(() => {
    const traceInterval = setInterval(() => {
      const randomTraces = Array.from({ length: 10 }, (_, i) => 
        `${employeeId}-${i}-${Math.random().toString(36).substring(7)}`
      )
      setTraceElements(randomTraces)
    }, 500)
    
    return () => clearInterval(traceInterval)
  }, [employeeId])
  
  useEffect(() => {
    let intervalId: number | null = null
    let countdownIntervalId: number | null = null
    let isActive = true // Флаг активности компонента
    
    const generateQRImage = async (token: string) => {
      // Увеличенный размер QR для лучшей читаемости
      const isMobile = window.innerWidth <= 480
      const qrSize = isMobile ? 350 : 400 // Увеличено с 280/300 до 350/400
      
      try {
        const dataUrl = await QRCode.toDataURL(token, {
          width: qrSize,
          margin: 2, // Минимальный отступ для лучшей читаемости камерой
          errorCorrectionLevel: 'M', // Medium error correction (лучший баланс)
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        if (isActive) {
          setQrImageUrl(dataUrl)
        }
      } catch (qrError) {
        console.error('Failed to generate QR image:', qrError)
      }
    }
    
    const MAX_RETRIES = 3
    const RETRY_DELAY = 2000 // 2 секунды
    
    const updateQR = async (isRetry = false) => {
      if (!isActive) return
      
      try {
        const result = await QRGenerator.getCurrentQR()
        if (!isActive) return
        
        setQrData(result)
        qrDataRef.current = result
        setError(null)
        retryCountRef.current = 0 // Сбрасываем счетчик при успехе
        
        // Генерируем QR изображение
        await generateQRImage(result.qrToken)
      } catch (err) {
        if (!isActive) return
        
        const errorMessage = err instanceof Error ? err.message : 'Ошибка генерации QR'
        setError(errorMessage)
        setQrData(null)
        qrDataRef.current = null
        setQrImageUrl(null)
        
        // Автоматическая повторная попытка с экспоненциальной задержкой
        if (!isRetry && retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++
          const delay = RETRY_DELAY * Math.pow(2, retryCountRef.current - 1) // Экспоненциальная задержка
          setTimeout(() => {
            if (isActive) {
              updateQR(true)
            }
          }, delay)
        }
      }
    }
    
    // Обработка фокуса окна
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Окно не в фокусе - приостанавливаем обновления
        isActive = false
      } else {
        // Окно в фокусе - возобновляем обновления
        isActive = true
        updateQR()
      }
    }
    
    // Генерируем QR сразу
    updateQR()
    
    // Обновляем каждые 10 секунд (fallback, основная логика в countdownInterval)
    intervalId = window.setInterval(() => {
      if (isActive && !document.hidden) {
        updateQR()
      }
    }, 10000)
    
    // Обновляем QR каждую секунду если нужно
    countdownIntervalId = window.setInterval(async () => {
      if (!isActive || document.hidden) return
      
      const current = qrDataRef.current
      if (current) {
        const now = Date.now()
        const nowSec = Math.floor(now / 1000)
        const slotEnd = current.payload.ts + 15
        const timeUntilRefresh = Math.max(0, slotEnd - nowSec)
        
        // Если время истекло, сразу обновляем QR
        if (timeUntilRefresh <= 0) {
          try {
            const newQR = await QRGenerator.getCurrentQR()
            if (isActive) {
              setQrData(newQR)
              qrDataRef.current = newQR
              await generateQRImage(newQR.qrToken)
            }
          } catch (err) {
            console.error('Failed to refresh QR:', err)
          }
        }
      }
    }, 1000)
    
    // Обработка изменения размера окна
    const handleResize = () => {
      if (!isActive) return
      const current = qrDataRef.current
      if (current) {
        generateQRImage(current.qrToken)
      }
    }
    
    window.addEventListener('resize', handleResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      isActive = false
      if (intervalId !== null) {
        clearInterval(intervalId)
      }
      if (countdownIntervalId !== null) {
        clearInterval(countdownIntervalId)
      }
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, []) // Убрали зависимость от qrData
  
  const handleRetry = async () => {
    setError(null)
    try {
      const result = await QRGenerator.getCurrentQR()
      setQrData(result)
      qrDataRef.current = result
      
      const isMobile = window.innerWidth <= 480
      const qrSize = isMobile ? 350 : 400
      const dataUrl = await QRCode.toDataURL(result.qrToken, {
        width: qrSize,
        margin: 2, // Минимальный отступ для лучшей читаемости камерой
        errorCorrectionLevel: 'M',
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      setQrImageUrl(dataUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка генерации QR')
    }
  }
  
  if (error) {
    return (
      <div className={`qr-display qr-display--error ${className || ''}`}>
        <div className="qr-display__error">{error}</div>
        <button className="qr-display__retry" onClick={handleRetry}>
          Повторить
        </button>
      </div>
    )
  }
  
  if (!qrData || !qrImageUrl) {
    return (
      <div className={`qr-display qr-display--loading ${className || ''}`}>
        <div className="qr-display__loading">Генерация QR-кода...</div>
      </div>
    )
  }
  
  return (
    <div className={`qr-display ${className || ''}`}>
      {/* QR код в белом блоке */}
      <div className="qr-display__code" style={{ position: 'relative' }}>
        <img
          src={qrImageUrl}
          alt="QR Code"
          className="qr-display__image"
        />
        
        {/* Dynamic watermark for screenshot tracing */}
        <div
          className="qr-watermark"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.01,
            mixBlendMode: 'overlay',
            pointerEvents: 'none',
            fontSize: '8px',
            color: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          {watermarkId}
        </div>
        
        {/* Hidden trace elements for screenshot detection */}
        {traceElements.map((trace, i) => (
          <div
            key={i}
            data-trace={trace}
            style={{ display: 'none' }}
          />
        ))}
      </div>
      
      {/* Таймер скрыт для пользователя, но QR обновляется автоматически */}
    </div>
  )
}

